import {
   FieldResponse,
   FieldTag,
   GeoShapeVariantType,
   OptionsValueType as GqlOptionsValueType,
   StageRecommendedTaskIdentifications
} from '@flowwai-frontend/bps-officer-web/graphql'
import { Sales_Crm_Web_CustomFieldResponseType_FragmentFragment } from '@flowwai-frontend/bps-officer-web/graphql-docs'
import {
   AttachmentModel,
   GeoLocationDetails,
   RelatedRecord,
   StageResponse,
   UnionFieldModelType,
   UnionFieldResponse,
   UnionFieldResponseType,
   User,
   LocationVariantType,
   OptionsValueType,
   StageTasksIdentifications,
   FieldTagType
} from '@flowwai-frontend/shared/sales-crm/sales-crm-core'
import { convertPipeLineTypeToPipeLineTypeEnum } from './pipeline'

export const getFieldResponsesBasedOnFieldType = (
   data: any, //FIXME: replace this any with Sales_Crm_Web_CustomFieldResponseType_FragmentFragment
   createOrUpdateUser?: (user: User) => void
): UnionFieldResponseType => {
   if (!data) return null

   const response = data as Sales_Crm_Web_CustomFieldResponseType_FragmentFragment

   switch (response.__typename) {
      case 'GQLStringType':
         return response.stringValue
      case 'GQLListStringType':
         return response.values
      case 'GQLDateTimeType':
         return response.dateTime ? new Date(response.dateTime) : undefined
      case 'GQLDateType':
         return response.date ? new Date(response.date) : undefined
      case 'GQLTimeType':
         return response.timeValue
      case 'GQLFloatType':
         return response.floatValue?.toString()
      case 'GQLIntType':
         return response.integerValue?.toString()
      case 'PhoneNumberResponse':
         return {
            dialCode: response.dialCode,
            iso2CountryCode: response.iso2CountryCode as string,
            phoneNumber: response.phoneNumber
         }
      case 'Stage':
         return {
            stageId: response.stageId,
            name: response.name,
            order: response.order,
            color: response.color ?? undefined,
            overviewFieldsConfig: data.overviewFieldsConfig,
            quickEditFields: data.quickEditFields,
            recommendedTaskTemplateIds: data.recommendedTaskTemplateIds,
            editableFieldIds: [],
            permittedGOFIds: [],
            requiredFieldIds:
               response.applicationConfig?.requiredFieldIds ?? [],
            paymentPermissions: response.feePaymentPermissions,
            stageTasksIdentifications: response.stageTasksIdentifications
               ? convertGqlStageTaskIdentificationsToStageTaskIdentifications(
                    response.stageTasksIdentifications as StageRecommendedTaskIdentifications[]
                 )
               : undefined,
            transitions: data.transitions,
            completedLayoutTabIds: response.completedLayoutTabIds,
            permittedLayoutTabIds: response.permittedLayoutTabIds,
            canMakeFieldsAsShortfall: response.applicationConfig
               ? response.applicationConfig?.canMakeApplicationFieldsAsShortfall
               : false,
            addPermittedAddAnotherGOFIds:
               response.userAddAnotherGofsPermissions
                  ?.allowedCreateAddAnotherGofIds,
            removePermittedAddAnotherGOFIds:
               response.userAddAnotherGofsPermissions
                  ?.allowedRemoveAddAnotherGofIds,
            hasNoteSheetAccess: response.hasNoteSheetAccess
         } as StageResponse
      case 'StageIsDeleted':
      case 'StageNotFound':
         return null

      case 'BaseUser': {
         const user: User = {
            userId: response.userId,
            email: response.email,
            name: response.userName,
            profilePicUrl: response.profilePicUrl,
            attendance: response.attendance
         }
         createOrUpdateUser && createOrUpdateUser(user)
         return user
      }

      case 'FileUploaderFieldResponse': {
         return response.urlResponses
            ? response.urlResponses.map(urlResponse =>
                 AttachmentModel.createAttachmentModel({
                    attachmentId: urlResponse.fileId,
                    name: urlResponse.name,
                    mimeType: urlResponse.mimeType,
                    sizeInBytes: urlResponse.sizeInBytes,
                    url: urlResponse.url,
                    preSignedUrl: urlResponse.url //Need to remove this key from the model
                 })
              )
            : []
      }
      case 'RelationFieldResponse': {
         const relatedRecords: RelatedRecord[] = response.pipelineItems.map(
            record => ({
               pipelineItemType: convertPipeLineTypeToPipeLineTypeEnum(
                  record.pipelineItemType
               ),
               pipelineItemId: record.pipelineItemId,
               pipelineId: record.pipelineId,
               name: record.name ?? '',
               hasPermission: record.hasPermission,
               isDeleted: record.isDeleted
            })
         )
         return relatedRecords
      }
      case 'GeoLocationFieldResponse': {
         const geoLocationDetails: GeoLocationDetails = {
            latitude: response.latitude,
            longitude: response.longitude
         }
         return geoLocationDetails
      }
      case 'GeoShapeFieldResponse': {
         if (!response.geoPoints?.length) return []

         const { geoPoints: locationPoints } = response
         const geoShapeResponse: GeoLocationDetails[] = locationPoints.map(
            point => ({
               latitude: point.latitude,
               longitude: point.longitude
            })
         )

         return geoShapeResponse
      }
      case 'ShortTextFieldResponse': {
         return response?.value
            ? {
                 prefix: response.prefix ?? undefined,
                 value: response.value,
                 suffix: response.suffix ?? undefined
              }
            : null
      }

      case 'BasePipeline':
      case 'PipelineItemOverviewDetails':
      default:
         throw new Error('Unhandled Field Response Type')
   }
}

export const convertGraphqlFieldResponseToUnionFieldResponse = (
   fieldModel: UnionFieldModelType,
   fieldResponse: FieldResponse
): UnionFieldResponse => ({
   fieldId: fieldResponse.fieldId,
   fieldResponseId: fieldResponse.fieldResponseId,
   fieldType: fieldModel.fieldType as any,
   response: fieldResponse.response
      ? getFieldResponsesBasedOnFieldType(fieldResponse.response)
      : (null as any)
})

export const convertGqlGeoLocationVariantTypeToLocationVariantType = (
   variant: GeoShapeVariantType
): LocationVariantType => {
   switch (variant) {
      case GeoShapeVariantType.Line:
         return 'LINE'
      case GeoShapeVariantType.MultiPoint:
         return 'MULTI_POINT'
      case GeoShapeVariantType.Polygon:
         return 'POLYGON'
      case GeoShapeVariantType.Point:
         return 'POINT'
   }
}

export const convertLocationVariantTypeToGqlLocationVariantType = (
   variant: LocationVariantType
): GeoShapeVariantType => {
   switch (variant) {
      case 'LINE':
         return GeoShapeVariantType.Line
      case 'MULTI_POINT':
         return GeoShapeVariantType.MultiPoint
      case 'POLYGON':
         return GeoShapeVariantType.Polygon
      case 'POINT':
         return GeoShapeVariantType.Point
   }
}

export const convertGqlConditionalOptionTypesToLocalType = (
   conditionalOptionType: GqlOptionsValueType
): OptionsValueType => {
   switch (conditionalOptionType) {
      case GqlOptionsValueType.ConditionalOptions:
         return 'CONDITIONAL_OPTIONS'
      case GqlOptionsValueType.Default:
         return 'DEFAULT'
      case GqlOptionsValueType.VisibilityRules:
         return 'VISIBILITY_RULES'
   }
}

export const convertGqlStageTaskIdentificationsToStageTaskIdentifications = (
   stageTasksIdentifications: StageRecommendedTaskIdentifications[]
): StageTasksIdentifications[] =>
   stageTasksIdentifications.map(stageTasksIdentification => ({
      taskTemplateId: stageTasksIdentification.taskTemplateId,
      identifications: stageTasksIdentification.identifications.map(
         identification => identification.identificationId
      )
   }))

export const convertGqlFieldTagToFieldTagType = (
   fieldTag: FieldTag
): FieldTagType | null => {
   switch (fieldTag) {
      case 'FEE_CALCULATION':
         return 'FEE_CALCULATION'

      default:
         return null
   }
}
